import { HeadlessUI, Hint } from '@systemeio/ui'
import AffiliateDashboardPromotionLink from 'modules/affiliate-dashboard/components/affiliate-dashboard-promotion-link'
import { useAffiliateDashboardPromotion } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-promotion'
import { AffiliateDashboardPromotionInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-promotion-interface'
import React, { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { ArrowDownIcon } from '@systemeio/ui'

interface AffiliatePromotionProps {
  affiliateId?: string
}

const AffiliateDashboardPromotion = ({ affiliateId }: AffiliatePromotionProps) => {
  const { t } = useLocoTranslation()

  const [opened, setOpened] = useState(false)

  const { data: affiliatePromotion } = useAffiliateDashboardPromotion(opened)

  return (
    <div
      className={
        'bg-background-secondary flex w-full flex-col gap-3 overflow-x-hidden rounded-lg px-6 py-4'
      }
    >
      <div className={'flex flex-col gap-1'}>
        <div className={'flex justify-between gap-3'}>
          <span className={'text-text-primary text-[17px] font-bold'}>
            {t('dashboard.affiliate_dashboard.promote_first-2')}
          </span>
          <button
            className={
              'ring-brand-primary flex items-center gap-1.5 rounded-md outline-hidden ring-offset-1 focus-visible:ring-2'
            }
            onClick={() => setOpened(prev => !prev)}
          >
            <span className={'text-brand-primary hidden lg:inline'}>
              {opened
                ? t('dashboard.affiliate_dashboard.hide_stats')
                : t('dashboard.affiliate_dashboard.show_stats')}
            </span>
            <ArrowDownIcon
              className={`transition-transform ${opened && 'rotate-180'} stroke-brand-primary`}
            />
          </button>
        </div>
        <span className={'text-text-primary'}>
          {t('dashboard.affiliate_dashboard.promote_second')}
        </span>
      </div>
      <AffiliateDashboardPromotionLink affiliateId={affiliateId} />
      <HeadlessUI.Transition
        show={opened}
        enter="ease-linear duration-200"
        enterFrom="-translate-y-1 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="ease-linear duration-200"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="-translate-y-1 opacity-0"
      >
        <div className={'flex justify-between gap-3'}>
          <AffiliatePromotionContent
            affiliatePromotion={affiliatePromotion}
            title={
              <Hint label={t('dashboard.affiliate.last_30_days')}>
                {t('dashboard.affiliate_dashboard.freemium_registrations')}
              </Hint>
            }
          >
            {affiliatePromotion => affiliatePromotion.freeRegistrationsCount}
          </AffiliatePromotionContent>
          <AffiliatePromotionContent
            affiliatePromotion={affiliatePromotion}
            title={
              <Hint label={t('dashboard.affiliate.current_active')}>
                {t('dashboard.affiliate_dashboard.active_customers')}
              </Hint>
            }
          >
            {affiliatePromotion => affiliatePromotion.paidCount}
          </AffiliatePromotionContent>
        </div>
      </HeadlessUI.Transition>
    </div>
  )
}

const AffiliatePromotionContent = ({
  affiliatePromotion,
  children,
  title,
}: {
  affiliatePromotion?: AffiliateDashboardPromotionInterface
  children: (affiliatePromotion: AffiliateDashboardPromotionInterface) => string | number
  title: string | JSX.Element
}) => (
  <div className={'flex w-1/2 flex-col justify-between gap-3'}>
    <span className={'text-text-primary'}>{title}</span>
    {affiliatePromotion ? (
      <span className={'text-text-primary text-[17px] font-bold'}>
        {children(affiliatePromotion)}
      </span>
    ) : (
      <span className={'bg-skeleton-primary w-fit animate-pulse rounded-lg px-5 text-transparent'}>
        .
      </span>
    )}
  </div>
)

export default AffiliateDashboardPromotion
